import {
  LayoutDashboard,
  ShoppingBag,
  Store,
  Map,
  LogOut,
  Bike,
  Menu,
  X as CloseIcon,
  History,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { UserAvatar } from "@/components/atoms/avatar";
import NavbarItemSettings from "./navbar/NavbarItemSettings";
import { formatUserName } from "@/lib/utils";
import React, { memo } from "react";
import { useSidebar } from "@/contexts/SidebarContext";
import { useFirstMount } from "@/hooks/useFirstMount";
import { useOrdersStore } from "@/pages/orders-new/use-orders-store";

// Memoizar el componente del sidebar
const AppSidebar = memo(() => {
  const location = useLocation();
  const { logout, user } = useAuth0();
  const { userRole, isDriver, supabaseInfo, userNickname } = useSidebar();
  const [open, setOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isFirstMount = useFirstMount();

  // Close sidebar when clicking outside (mobile, open)
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Prevent scroll when sidebar is open on mobile
  useEffect(() => {
    if (open && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [open]);

  // Handle window resize
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 768) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sidebar width
  const miniWidth = "w-14"; // 56px
  const fullWidth = "w-64"; // 256px

  // Memoizar las secciones para evitar recalcular en cada render
  // Remove the 'active' property from the memoized sections, so that active is always determined at render time
  const sections = useMemo(() => {
    const allSections = [
      {
        icon: LayoutDashboard,
        label: "Dashboard",
        to: "/",
        allowedRoles: ["manager", "admin"],
      },
      {
        icon: ShoppingBag,
        label: "Órdenes",
        to: "/orders",
        allowedRoles: ["manager", "admin"],
      },

      {
        icon: History,
        label: "Historial",
        to: "/orders/historical",
        allowedRoles: ["manager", "admin"],
      },
      {
        icon: Store,
        label: "Negocios",
        to: "/brands",
        allowedRoles: ["manager", "admin"],
      },
      {
        icon: Map,
        label: "Mapa",
        to: "/map",
        allowedRoles: ["manager", "admin"],
      },
      {
        icon: Bike,
        label: "Conductores",
        to: "/drivers",
        allowedRoles: ["manager", "admin"],
      },
      {
        icon: User,
        label: "Portal del Conductor",
        to: "/driver-portal",
        allowedRoles: ["driver"],
      },
    ];

    return userRole === null
      ? []
      : isDriver
        ? allSections.filter((section) =>
            section.allowedRoles.includes("driver"),
          )
        : allSections.filter(
            (section) =>
              section.allowedRoles.includes("customer") ||
              section.allowedRoles.includes("admin"),
          );
  }, [userRole, isDriver]);

  // Sidebar content
  const SidebarContent = (
    <>
      {/* Top logo + toggle button */}
      <div className="flex flex-col-reverse items-center gap-2 px-2 py-2 md:px-1 md:py-0 md:pb-2">
        <div
          className={cn(
            "flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-0 w-full",
            open
              ? "justify-start px-2"
              : "justify-center items-center md:justify-start md:px-2",
          )}
        >
          <img
            src="/home-logo.svg"
            alt="Gonau"
            className="h-8 w-8 rounded-xl object-contain shadow-md"
          />
          <motion.div
            className={cn(
              "md:ml-3 flex-col gap-1",
              open ? "flex" : "hidden md:flex",
            )}
            initial={
              isFirstMount ? { opacity: 0, x: -20 } : { opacity: 1, x: 0 }
            }
            animate={{ opacity: 1, x: 0 }}
            transition={isFirstMount ? { duration: 0.2 } : { duration: 0 }}
          >
            <span className="text-white font-medium text-sm">Gonau</span>
            <span className="text-white/60 text-xs">{user?.email}</span>
          </motion.div>
        </div>
        {/* Toggle button (mobile only) */}
        <div
          className={cn(
            "flex items-start justify-start w-full md:hidden",
            open ? "justify-end px-2" : "justify-center",
          )}
        >
          {" "}
          <button
            className={cn(
              "md:hidden flex items-center justify-center w-8 h-8 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/20",
              open ? "bg-white/10 " : "",
            )}
            onClick={() => setOpen((prev) => !prev)}
            aria-label={open ? "Cerrar menú lateral" : "Abrir menú lateral"}
            type="button"
          >
            {open ? <CloseIcon size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <div className="border-b border-white/20 my-2 mx-2" />
      {/* Navigation */}
      <nav className="flex-1 flex flex-col gap-2 sm:gap-1 mx-2 sm:mx-1">
        {userRole === null ? (
          // Show loading state while user role is being determined
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/30 border-t-white"></div>
          </div>
        ) : (
          sections.map((section) => {
            // Determine active state at render time, not in useMemo
            let isActive = false;
            if (section.to === "/") {
              isActive = location.pathname === "/";
            } else if (section.to === "/brands" || section.to === "/drivers") {
              isActive = location.pathname.startsWith(section.to);
            } else if (section.to === "/orders") {
              isActive =
                location.pathname !== "/orders/historical" &&
                location.pathname.startsWith("/orders");
            } else {
              isActive = location.pathname === section.to;
            }
            return (
              <SidebarNavItem
                key={section.label}
                icon={section.icon}
                label={section.label}
                to={section.to}
                active={isActive}
                expanded={open || window.innerWidth >= 768}
              />
            );
          })
        )}
      </nav>
      {/* Bottom section */}
      <div className="mt-auto">
        {/* <div className="px-4 mb-2 flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
            <span className="text-xl">🇸🇻</span>
          </div>
          {(open || window.innerWidth >= 768) && (
            <span className="text-white/80 text-sm">El Salvador</span>
          )}
        </div> */}
        <nav className="flex-1 flex flex-col mx-2 mb-2">
          <NavbarItemSettings expanded={open || window.innerWidth >= 768} />
        </nav>
        <div className="border-t border-white/20 mx-2 mb-2" />
        <Link
          to="/my-profile"
          className={cn(
            "flex items-center gap-3 text-sm h-10 mx-2 rounded-xl",
            "flex items-center gap-2 px-2 py-3 cursor-pointer group transition-all text-white/80 hover:text-white hover:bg-white/10  duration-200",
            "focus:outline-none focus:ring-2 focus:ring-white/20",
            open || window.innerWidth >= 768
              ? "justify-start"
              : "justify-center",
          )}
        >
          <div className="shadow-md hover:shadow-lg transition-shadow duration-200">
            <UserAvatar
              user={supabaseInfo || user || {}}
              size="sm"
              fallbackSrc="/driver-illustration/profile-pic-default.svg"
            />
          </div>
          {(open || window.innerWidth >= 768) && (
            <div className="flex flex-col">
              <span className="text-white font-medium text-sm">
                {userNickname ||
                  formatUserName(
                    supabaseInfo?.first_name,
                    supabaseInfo?.last_name,
                  ) ||
                  (user?.name?.includes("@")
                    ? user.name.split("@")[0]
                    : user?.name) ||
                  "Usuario"}
              </span>
            </div>
          )}
        </Link>
        <div
          className={cn(
            "flex items-center px-3",
            open || window.innerWidth >= 768
              ? " justify-start"
              : "justify-center",
          )}
        >
          <button
            className={cn(
              "flex items-center gap-3 text-sm px-3 h-10 rounded-xl w-[40px] sm:w-auto",
              "text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-white/20",
              open && "w-full",
            )}
            onClick={(e) => {
              e.preventDefault();
              logout({ logoutParams: { returnTo: window.location.origin } });
            }}
            aria-label="Cerrar sesión"
          >
            <LogOut size={20} className="flex-shrink-0" />{" "}
            {(open || window.innerWidth >= 768) && (
              <span className="text-white font-medium text-sm w-auto">
                Cerrar sesión
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Overlay when open on mobile */}
      <div
        className={cn(
          "fixed inset-0 z-30 bg-black/40 transition-opacity duration-300 md:hidden",
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        aria-hidden={!open}
        onClick={() => setOpen(false)}
      />
      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={cn(
          "fixed top-0 left-0 z-40 h-screen flex flex-col justify-between bg-[#4618b0] py-2 transition-all duration-300",
          miniWidth,
          open && fullWidth,
          open ? "shadow-2xl" : "shadow-md",
          "md:fixed md:z-10 md:h-screen md:shadow-none md:py-4 md:flex md:flex-col",
          "md:w-[240px]",
        )}
        style={{
          width:
            open && window.innerWidth < 768
              ? 256
              : window.innerWidth >= 768
                ? 240
                : 56,
        }}
      >
        {SidebarContent}
      </aside>
    </>
  );
});

AppSidebar.displayName = "AppSidebar";

// Memoizar también el SidebarNavItem
const SidebarNavItem = memo(
  ({
    icon: Icon,
    label,
    to,
    active = false,
    expanded = false,
  }: {
    icon: React.ElementType;
    label: string;
    to: string;
    active?: boolean;
    expanded?: boolean;
  }) => {
    const { removeSelectedOrder } = useOrdersStore();
    return (
      <Link
        to={to}
        onClick={() => {
          if (to === "/orders") {
            removeSelectedOrder();
          }
        }}
        className={cn(
          "flex items-center gap-1 h-10 rounded-xl px-3 transition-colors duration-200",
          "text-white/80 hover:text-white hover:bg-white/10",
          active && "bg-white text-[#141C25]",
          expanded ? "justify-start w-full" : "justify-center w-10 mx-auto",
          "focus:outline-none focus:ring-2 focus:ring-white/20",
        )}
        aria-current={active ? "page" : undefined}
      >
        <Icon size={22} className="flex-shrink-0" />
        {expanded && <span className="text-sm font-medium ml-2">{label}</span>}
      </Link>
    );
  },
);

export default AppSidebar;
